import React, { useState, useEffect, useRef } from 'react';
import { Box, Button, Collapse, Typography } from '@mui/material';
import { IMortgage } from '../../../../../../Interfaces/IMortgage';
import CompanyModifiedBaseInformation from '../Components/Owner/CompanyModifiedBaseInformation';
import { GridBaseData } from '../../../../../Person/GridBaseData';
import { getNewInheritPerson } from '../../../../../../services/dialog.service';
import { IPerson } from '../../../../../../Interfaces/IPerson';
import { ICompany, getNewCompany } from '../../../../../../Interfaces/ICompany';

interface IProps {
  handleBack: Function;
  handleNext: Function;
  mortgageObject: IMortgage;
  setMortgageObject: Function;
}

export const MortgageBeneficiary: React.FC<IProps> = ({
  handleBack,
  handleNext,
  mortgageObject,
  setMortgageObject,
}) => {
  const formRef = useRef<HTMLFormElement>(null);

  const [isCompany, setIsCompany] = useState<boolean | null>(
    mortgageObject.BeneficiaryDetails &&
      ('idPerson' in mortgageObject.BeneficiaryDetails ||
        'idCompany' in mortgageObject.BeneficiaryDetails)
      ? 'idCompany' in mortgageObject.BeneficiaryDetails
      : null
  );

  const [creditor, setCreditor] = useState<IPerson | ICompany | null>(
    mortgageObject.BeneficiaryDetails || null
  );

  const handleRadioChange = (value: boolean) => {
    setIsCompany(value);

    setMortgageObject({
      ...mortgageObject,
      BeneficiaryDetails: value ? getNewCompany() : getNewInheritPerson,
    });

    setCreditor(value ? getNewCompany() : getNewInheritPerson);
  };
  const handleSubmit = () => {
    handleNext();
  };
  const handleOnSave = () => {
    if (formRef.current && formRef.current.checkValidity()) {
      handleSubmit();
    } else if (formRef.current && !formRef.current.checkValidity()) {
      const elements = Array.from(formRef.current.elements).reverse();

      elements.forEach((element: any) => {
        if (element.reportValidity()) {
          element.reportValidity();
        }
      });
    }
  };

  const hasPostcode =
    mortgageObject.BeneficiaryDetails?.idPostcode !== undefined &&
    mortgageObject.BeneficiaryDetails?.idPostcode !== null;

  const isCompanyValid =
    isCompany &&
    creditor !== null &&
    (creditor as ICompany).Company?.trim() !== '';

  const isPersonValid =
    !isCompany &&
    creditor !== null &&
    (creditor as IPerson).idGender !== -1 &&
    (creditor as IPerson).FirstName?.trim() !== '' &&
    (creditor as IPerson).LastName?.trim() !== '' &&
    (creditor as IPerson).Birthday?.trim() !== '' &&
    (creditor as IPerson).Street?.trim() !== '' &&
    (creditor as IPerson).StreetNr?.trim() !== '';

  const isFormValid = hasPostcode && (isCompanyValid || isPersonValid);

  useEffect(() => {
    setMortgageObject({
      ...mortgageObject,
      BeneficiaryDetails: {
        ...creditor,
      },
    });
  }, [creditor]);

  return (
    <>
      <form ref={formRef}>
        <Typography mt={6} variant='h4'>
          Gläubiger
        </Typography>
        <Typography mt={2} mb={2}>
          {mortgageObject.ToBeDeleted
            ? 'Bitte geben Sie die vollständigen Daten des ursprünglichen Kreditgebers (Gläubigers) an, die bei der Eintragung der Grundschuld verwendet wurden. Bei Unternehmen nennen Sie bitte den Firmennamen sowie die damalige Geschäftsadresse.'
            : 'Bitte geben Sie die vollständigen Angaben zum Gläubiger (Kreditgeber) an. Ist der Kreditgeber ein Unternehmen, tragen Sie bitte den Firmennamen sowie die vollständige Geschäftsadresse ein.'}
        </Typography>

        <Box>
          <Box
            sx={{
              display: 'flex',
              border: '1px solid',
              borderColor: 'text.primary',
              borderTopLeftRadius: 8,
              borderTopRightRadius: 8,
              ...(isCompany === null
                ? { borderBottomLeftRadius: 8, borderBottomRightRadius: 8 }
                : { borderBottom: '1px solid #ccc' }),
              overflow: 'hidden',
            }}
          >
            <Button
              variant={
                isCompany === null
                  ? 'outlined'
                  : isCompany
                    ? 'contained'
                    : 'outlined'
              }
              onClick={() => handleRadioChange(true)}
              sx={{
                border: 'none',
                borderColor: 'text.primary',
                ...(isCompany
                  ? {}
                  : {
                      color: 'text.primary',
                      borderRight: '1px solid #ccc',
                    }),
                flex: 1,
                borderRadius: 0,
                '&:hover': {
                  borderColor: 'white',
                },
              }}
            >
              Gläubiger ist ein Unternehmen.
            </Button>
            <Button
              variant={
                isCompany === null
                  ? 'outlined'
                  : !isCompany
                    ? 'contained'
                    : 'outlined'
              }
              onClick={() => handleRadioChange(false)}
              sx={{
                border: 'none',
                borderColor: 'text.primary',
                ...(isCompany !== null && !isCompany
                  ? {}
                  : {
                      color: 'text.primary',
                      borderLeft: '1px solid #ccc',
                    }),
                flex: 1,
                borderRadius: 0,
                '&:hover': {
                  borderColor: 'white',
                },
              }}
            >
              Gläubiger ist eine Privatperson.
            </Button>
          </Box>
          {isCompany !== null && (
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'text.primary',
                borderTop: 'none',
                borderBottomLeftRadius: 8,
                borderBottomRightRadius: 8,
                p: 2,
                minHeight: 350,
              }}
            >
              <>
                <Collapse in={isCompany}>
                  {isCompany && mortgageObject.BeneficiaryDetails && (
                    <CompanyModifiedBaseInformation
                      companyObject={creditor as ICompany}
                      setCompanyObject={setCreditor}
                    />
                  )}
                </Collapse>
                <Collapse in={!isCompany}>
                  {!isCompany && mortgageObject.BeneficiaryDetails && (
                    <GridBaseData
                      personObject={creditor as IPerson}
                      personArray={[]}
                      setPersonObject={setCreditor}
                      disableContactInformations={true}
                      mt={0}
                    />
                  )}
                </Collapse>
              </>
            </Box>
          )}
        </Box>

        <Box mt={6} sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
          <Button
            variant='outlined'
            onClick={() => handleBack()}
            sx={{ mr: 1 }}
          >
            Zurück
          </Button>
          <Box sx={{ flex: '1 1 auto' }} />
          <Button
            onClick={() => {
              handleOnSave();
            }}
            disabled={isCompany === null}
            variant={isFormValid ? 'contained' : 'outlined'}
          >
            Weiter
          </Button>
        </Box>
      </form>
    </>
  );
};
